import React, { useEffect, useRef } from 'react';
import { Label } from 'reactstrap';
import Checkbox, { CheckBoxItem } from '../Checkbox';
import './CheckBoxesList.scss';

export type CheckboxListItem = CheckBoxItem & {
  key: string;
};

type Props = {
  items: CheckboxListItem[];
  disabled: boolean;
  onChangeCheckbox: (items: CheckboxListItem[]) => void;
  noSelectAll?: boolean;
  isOnChangeInOnClick?: boolean;
};

const useCheckBoxesListProps = ({ items, onChangeCheckbox }: Props) => {
  const allCheckboxRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (allCheckboxRef.current) {
      const indeterminate =
        items.some(i => i.isChecked) && items.some(i => !i.isChecked);
      allCheckboxRef.current.indeterminate = indeterminate;
    }
  }, [allCheckboxRef.current, items]);

  const onLocalSelectAll = async (checked: boolean) => {
    const updatedItems = items.map<CheckboxListItem>(item => ({
      ...item,
      isChecked: item.disabled ? item.isChecked : checked,
    }));

    await onChangeCheckbox(updatedItems);
  };

  const onLocalChangeCheckbox = async (
    isChecked: boolean,
    itemKey?: string,
  ) => {
    const updatedItems = items.map<CheckboxListItem>(item => {
      if (item.key === itemKey) {
        return {
          ...item,
          isChecked,
        };
      }

      return item;
    });

    await onChangeCheckbox(updatedItems);
  };

  return {
    allCheckboxRef,
    onLocalSelectAll,
    onLocalChangeCheckbox,
  };
};

export const CheckBoxesList = (props: Props) => {
  const { items, disabled, noSelectAll, isOnChangeInOnClick } = props;
  const { allCheckboxRef, onLocalSelectAll, onLocalChangeCheckbox } =
    useCheckBoxesListProps(props);
  return (
    <div className="checkbox-list">
      {!noSelectAll && (
        <div className="checkbox-list__all-input-wrapper">
          <Label>
            <input
              className="checkbox-list__all-input"
              type="checkbox"
              disabled={disabled}
              checked={items.every(i => i.disabled || i.isChecked || false)}
              onChange={e => onLocalSelectAll(e.target.checked)}
              ref={allCheckboxRef}
            />
            <span>Select&nbsp;All</span>
          </Label>
        </div>
      )}
      <div className="checkbox-list_inputs-block">
        {items.map(item => (
          <Checkbox
            className="checkbox-list__checkbox"
            key={item.key}
            disabled={disabled || item.disabled}
            isChecked={item.isChecked}
            onToggle={e => onLocalChangeCheckbox(e.target.checked, item.key)}
            onChangeInOnClick={
              isOnChangeInOnClick
                ? isChecked => onLocalChangeCheckbox(isChecked, item.key)
                : undefined
            }
            label={item.label}
          />
        ))}
      </div>
    </div>
  );
};
